import React, { useState, useEffect } from 'react';
import { getKycDocuments, uploadKycDocument, getMerchantProfile } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Upload, CheckCircle, XCircle, Clock } from 'lucide-react';

const documentTypes = [
  { type: 'AADHAAR_CARD', label: 'Aadhaar Card', required: true },
  { type: 'COMPANY_GST', label: 'Company GST Certificate', required: true },
  { type: 'COMPANY_PAN', label: 'Company PAN Card', required: true },
  { type: 'DIRECTOR_PAN', label: "Director's PAN Card", required: true },
  { type: 'SIGNED_AGREEMENT', label: 'Signed Agreement', required: false },
];

const KycDocuments = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  useEffect(() => { 
    loadProfile();
    loadDocs(); 
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMerchantProfile();
      setProfileData(response.data);
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
    }
  };

  const loadDocs = async () => {
    try { const res = await getKycDocuments(); setDocs(res.data || []); } 
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    setUploading(type);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await uploadKycDocument(type, formData);
      showSuccessToast('Document uploaded successfully');
      // Reload profile data to get updated KYC status
      await loadProfile();
      loadDocs();
    } catch (err) { 
      console.error('Upload error:', err);
      showErrorToast('Upload failed'); 
    } finally { 
      setUploading(null); 
    }
  };

  const getDocStatus = (type) => {
    const doc = docs.find(d => d.documentType === type);
    if (!doc) return { status: 'pending', doc: null };
    if (doc.isVerified) return { status: 'verified', doc };
    if (doc.rejectionReason) return { status: 'rejected', doc };
    return { status: 'submitted', doc };
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">KYC Documents</h1>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm">KYC Status:</span>
            <Badge className={profileData?.kycStatus === 'APPROVED' ? 'bg-green-100 text-green-800' : profileData?.kycStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
              {profileData?.kycStatus || user?.kycStatus || 'PENDING'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {documentTypes.map(({ type, label, required }) => {
          const { status, doc } = getDocStatus(type);
          return (
            <Card key={type} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start sm:items-center gap-3">
                    <div className="mt-1 sm:mt-0">
                      {status === 'verified' ? <CheckCircle className="h-5 w-5 text-green-500" /> :
                       status === 'rejected' ? <XCircle className="h-5 w-5 text-red-500" /> :
                       status === 'submitted' ? <Clock className="h-5 w-5 text-amber-500" /> :
                       <Upload className="h-5 w-5 text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{label} {required && <span className="text-red-500">*</span>}</p>
                      {doc && <p className="text-sm text-gray-500 truncate">{doc.fileName}</p>}
                      {doc?.rejectionReason && <p className="text-sm text-red-500 mt-1">↳ {doc.rejectionReason}</p>}
                    </div>
                  </div>
                  <div className="flex sm:block justify-end">
                    <Input
                      ref={(input) => {
                        if (!window.fileInputs) window.fileInputs = {};
                        window.fileInputs[type] = input;
                      }}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      className="hidden"
                      id={`upload-${type}`}
                      onChange={(e) => handleUpload(type, e.target.files[0])}
                      disabled={uploading === type}
                    />
                    <Button 
                      variant={status === 'verified' ? 'outline' : 'default'} 
                      disabled={uploading === type}
                      onClick={() => window.fileInputs[type]?.click()}
                      className="w-full sm:w-auto"
                    >
                      {uploading === type ? 'Uploading...' : status === 'pending' ? 'Upload' : 'Re-upload'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default KycDocuments;
